import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, UserPlusIcon, CheckIcon, UsersIcon } from 'lucide-react';
import { StepProps, TITLE_PROFILE_MAP } from '../../types/onboarding';
interface OnboardingEmployee {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  role: string;
  phone: string;
  mobile: string;
  applicationDate: string;
}
// Title -> Role mapping for generated mock data
const TITLE_ROLE_MAP: Record<string, string> = {
  'Loan Officer': 'Loan Officer',
  'Sr Processor': 'Processing Director',
  'Jr Processor': 'Jr Processor',
  'Team Lead': 'Team Lead',
  'Division Lead': 'Division Lead',
  'Loan Associate': 'Loan Officer',
  'Doc Specialist': 'Processor',
  'IT Team Member': 'IT',
  'Human Resources': 'HR',
  'Training Team': 'Training',
  Admin: 'IT'
};
const FIRST_NAMES = ['John', 'Emily', 'Michael', 'Sarah', 'David', 'Jessica', 'Daniel', 'Ashley', 'Christopher', 'Amanda', 'Matthew', 'Brittany', 'Joshua', 'Samantha', 'Andrew', 'Megan', 'Joseph', 'Rachel', 'Ryan', 'Lauren', 'Brandon', 'Stephanie', 'Justin', 'Nicole', 'William', 'Hannah', 'Kevin', 'Victoria', 'Brian', 'Olivia'];
const LAST_NAMES = ['Smith', 'Johnson', 'Brown', 'Williams', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson'];
const TITLES = Object.keys(TITLE_ROLE_MAP);
function pad(n: number) {
  return n.toString().padStart(4, '0');
}
// Deterministic mock dataset (~64 employees in Onboarding status)
const ONBOARDING_EMPLOYEES: OnboardingEmployee[] = Array.from({
  length: 64
}, (_, i) => {
  const firstName = FIRST_NAMES[i % FIRST_NAMES.length];
  const lastName = LAST_NAMES[(i * 7 + 3) % LAST_NAMES.length];
  const title = TITLES[i % TITLES.length];
  const role = TITLE_ROLE_MAP[title];
  const phone = `(555) ${pad((i * 31 + 100) % 9000 + 100).slice(0, 3)}-${pad((i * 53 + 12) % 9000 + 1000)}`;
  const mobile = `(555) ${pad((i * 47 + 250) % 9000 + 100).slice(0, 3)}-${pad((i * 19 + 88) % 9000 + 1000)}`;
  const d = new Date(2026, 4, 1);
  d.setDate(d.getDate() - i * 3 % 40);
  const applicationDate = d.toISOString().slice(0, 10);
  return {
    id: `emp-${pad(i + 1)}`,
    firstName,
    lastName,
    title,
    role,
    phone,
    mobile,
    applicationDate
  };
});
function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function initials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}
export function SelectEmployee({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(data.selectedEmployeeId || null);
  // This step is optional — proceeding manually is always valid
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return ONBOARDING_EMPLOYEES;
    return ONBOARDING_EMPLOYEES.filter(emp => {
      return `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(q) || emp.phone.toLowerCase().includes(q) || emp.role.toLowerCase().includes(q);
    });
  }, [searchQuery]);
  const handleSelect = (emp: OnboardingEmployee) => {
    if (selectedId === emp.id) {
      // Deselect
      setSelectedId(null);
      updateData({
        firstName: '',
        lastName: '',
        title: '',
        role: '',
        phone: '',
        mobile: '',
        profile: '',
        selectedEmployeeId: ''
      });
    } else {
      setSelectedId(emp.id);
      updateData({
        firstName: emp.firstName,
        lastName: emp.lastName,
        title: emp.title,
        role: emp.role,
        phone: emp.phone,
        mobile: emp.mobile,
        profile: TITLE_PROFILE_MAP[emp.title] || '',
        selectedEmployeeId: emp.id
      });
    }
  };
  const handleManual = () => {
    setSelectedId(null);
    updateData({
      selectedEmployeeId: ''
    });
  };
  return <div className="space-y-5" data-id="element-358">
      {/* Intro */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3" data-id="element-359">
        <UserPlusIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-360" />
        <div data-id="element-361">
          <h3 className="text-sm font-medium text-blue-900" data-id="element-362">
            Select an Employee in Onboarding Status
          </h3>
          <p className="text-sm text-blue-700 mt-1" data-id="element-363">
            These hired candidates are queued for system provisioning. Selecting
            one auto-fills their User Details. If they aren't listed, continue
            to create the employee manually.
          </p>
        </div>
      </div>

      {/* Toolbar: count + search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-364">
        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-365">
          <UsersIcon className="w-4 h-4 text-gray-400" data-id="element-366" />
          <span className="font-medium text-gray-900" data-id="element-367">
            {ONBOARDING_EMPLOYEES.length}
          </span>
          <span data-id="element-368">employees in Onboarding status</span>
        </div>
        <div className="relative w-full sm:w-72" data-id="element-369">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-370">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-371" />
          </div>
          <input type="text" placeholder="Search name, phone, role..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#2563EB] focus:border-[#2563EB] text-sm" data-id="element-372" />
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-373">
        <div className="max-h-[420px] overflow-auto" data-id="element-374">
          <table className="min-w-full divide-y divide-gray-100" data-id="element-375">
            <thead className="bg-gray-50 sticky top-0 z-10" data-id="element-376">
              <tr data-id="element-377">
                <th className="w-10 px-4 py-3" data-id="element-378" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-379">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-380">
                  Phone Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-381">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-382">
                  Application Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white" data-id="element-383">
              {filteredEmployees.length > 0 ? filteredEmployees.map(emp => {
              const isSelected = selectedId === emp.id;
              return <tr key={emp.id} onClick={() => handleSelect(emp)} className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-384">
                      <td className="px-4 py-3" data-id="element-385">
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-[#2563EB] bg-[#2563EB]' : 'border-gray-300'}`} data-id="element-386">
                          {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-387" />}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-388">
                        <div className="flex items-center gap-3" data-id="element-389">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${isSelected ? 'bg-[#2563EB] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-390">
                            {initials(emp.firstName, emp.lastName)}
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-391">
                            {emp.firstName} {emp.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap" data-id="element-392">
                        {emp.phone}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap" data-id="element-393">
                        {emp.role}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap" data-id="element-394">
                        {formatDate(emp.applicationDate)}
                      </td>
                    </tr>;
            }) : <tr data-id="element-395">
                  <td colSpan={6} className="px-4 py-12 text-center" data-id="element-396">
                    <UsersIcon className="mx-auto h-10 w-10 text-gray-300" data-id="element-397" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-398">
                      No employees found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-399">
                      No onboarding employees match your search.
                    </p>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual creation option */}
      <button type="button" onClick={handleManual} className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed text-left transition-all ${selectedId === null ? 'border-[#2563EB] bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-400">
        <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-401">
          <UserPlusIcon className="w-4 h-4 text-gray-500" data-id="element-402" />
        </div>
        <div data-id="element-403">
          <p className="text-sm font-medium text-gray-900" data-id="element-404">
            Create a new employee manually
          </p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-405">
            Continue to User Details and enter the information yourself.
          </p>
        </div>
      </button>

      {/* Selection confirmation */}
      {selectedId && <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex items-center gap-2 text-sm text-[#2563EB] font-medium" data-id="element-406">
          <CheckIcon className="w-4 h-4" data-id="element-407" />
          {data.firstName} {data.lastName}'s details will be auto-filled in the
          next step.
        </motion.div>}
    </div>;
}